import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, ChevronLeft, Trash2, Check, Calendar, Users, Pencil, FileText } from "lucide-react";
import { toast } from "sonner";
import {
  getRetreat, updateRetreat, deleteRetreat,
  createRetreatCost, deleteRetreatCost,
  createRetreatPayment, updateRetreatPayment, deleteRetreatPayment,
  fmt, fmtDate,
} from "@/lib/api";

const STATUS_STYLES = {
  planned: "bg-slate-50 text-slate-700 border-slate-200",
  confirmed: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-600 border-red-200",
};

const TYPE_LABEL = {
  prepayment: "Prepayment",
  final: "Final",
  custom: "Custom",
};

export default function RetreatDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [costOpen, setCostOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [newCost, setNewCost] = useState({ name: "", amount: "" });
  const [newPay, setNewPay] = useState({ type: "prepayment", amount: "", due_date: "", paid: false });
  const [notesDraft, setNotesDraft] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState({ name: "", start_date: "", end_date: "", participants: 0 });

  const load = async () => {
    const d = await getRetreat(id);
    setData(d);
    setNotesDraft(d.notes || "");
    setNotesDirty(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) {
    return <div className="p-8 text-slate-500">Loading…</div>;
  }
  const s = data.summary || {};

  const handleAddCost = async () => {
    const amt = parseFloat(newCost.amount);
    if (!newCost.name.trim() || !amt || amt <= 0) return toast.error("Name and amount required");
    try {
      await createRetreatCost({ retreat_id: id, name: newCost.name.trim(), amount: amt });
      toast.success("Cost added");
      setCostOpen(false);
      setNewCost({ name: "", amount: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add cost"); }
  };
  const handleDeleteCost = async (cid) => {
    try { await deleteRetreatCost(cid); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const handleAddPayment = async () => {
    const amt = parseFloat(newPay.amount);
    if (!amt || amt <= 0) return toast.error("Amount required");
    try {
      await createRetreatPayment({
        retreat_id: id,
        type: newPay.type,
        amount: amt,
        due_date: newPay.due_date || "",
        paid: !!newPay.paid,
      });
      toast.success("Payment added");
      setPayOpen(false);
      setNewPay({ type: "prepayment", amount: "", due_date: "", paid: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add payment"); }
  };
  const togglePaid = async (p) => {
    try { await updateRetreatPayment(p.id, { paid: !p.paid }); load(); }
    catch { toast.error("Failed"); }
  };
  const handleDeletePayment = async (pid) => {
    try { await deleteRetreatPayment(pid); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveNotes = async () => {
    try {
      await updateRetreat(id, { notes: notesDraft });
      toast.success("Notes saved");
      setNotesDirty(false);
      load();
    } catch (e) { toast.error("Failed to save notes"); }
  };
  const handleStatus = async (v) => {
    try { await updateRetreat(id, { status: v }); load(); }
    catch { toast.error("Failed"); }
  };
  const handleDeleteRetreat = async () => {
    try {
      await deleteRetreat(id);
      toast.success("Retreat deleted");
      navigate("/partners");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete retreat");
    }
  };
  const openEdit = () => {
    setEditDraft({
      name: data.name || "",
      start_date: data.start_date || "",
      end_date: data.end_date || "",
      participants: data.participants ?? 0,
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    const name = editDraft.name.trim();
    if (!name) return toast.error("Name required");
    if (!editDraft.start_date || !editDraft.end_date) return toast.error("Dates required");
    if (editDraft.end_date < editDraft.start_date) return toast.error("End date must be on or after start");
    const participants = parseInt(editDraft.participants);
    if (Number.isNaN(participants) || participants < 0) return toast.error("Participants must be 0 or more");
    try {
      await updateRetreat(id, {
        name,
        start_date: editDraft.start_date,
        end_date: editDraft.end_date,
        participants,
      });
      toast.success("Retreat updated");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update retreat");
    }
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <Link to="/partners" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-2" data-testid="retreat-back-btn">
          <ChevronLeft size={12} /> Partners
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900" data-testid="retreat-name">{data.name}</h1>
              <Button
                variant="ghost"
                size="sm"
                onClick={openEdit}
                className="h-8 w-8 p-0 text-slate-400 hover:text-slate-900"
                title="Edit retreat details"
                data-testid="retreat-edit-btn"
              >
                <Pencil size={14} />
              </Button>
            </div>
            <div className="text-sm text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
              <span className="font-medium text-slate-700">{data.partner?.name}</span>
              <span className="flex items-center gap-1"><Calendar size={12} /> {fmtDate(data.start_date)} → {fmtDate(data.end_date)}</span>
              <span className="flex items-center gap-1"><Users size={12} /> {data.participants} participant{data.participants === 1 ? "" : "s"}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => window.open(`/retreat-bill/${id}`, "_blank")}
              className="h-10"
              data-testid="retreat-account-summary-btn"
            >
              <FileText size={14} /> Account Summary
            </Button>
            <Select value={data.status} onValueChange={handleStatus}>
              <SelectTrigger className={`h-10 text-xs uppercase tracking-wider w-[160px] ${STATUS_STYLES[data.status] || ""}`} data-testid="retreat-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="planned">Planned</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(true)}
              className="h-10 text-red-600 hover:text-red-700 hover:bg-red-50 border-slate-200"
              data-testid="retreat-delete-btn"
            >
              <Trash2 size={14} /> Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Financial summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="retreat-summary">
        <Card className="p-5 border-slate-200 shadow-none">
          <div className="text-xs uppercase tracking-wider text-slate-500">Total</div>
          <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{fmt(s.total || 0)}</div>
        </Card>
        <Card className="p-5 border-slate-200 shadow-none">
          <div className="text-xs uppercase tracking-wider text-emerald-700">Paid</div>
          <div className="font-heading text-2xl font-bold text-emerald-700 mt-1">{fmt(s.paid || 0)}</div>
        </Card>
        <Card className="p-5 border-slate-200 shadow-none">
          <div className="text-xs uppercase tracking-wider text-amber-700">Remaining</div>
          <div className="font-heading text-2xl font-bold text-amber-700 mt-1">{fmt(s.remaining || 0)}</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Cost breakdown */}
        <Card className="border-slate-200 shadow-none overflow-hidden" data-testid="cost-card">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="font-heading font-semibold text-slate-900">Cost Breakdown</div>
              <div className="text-xs text-slate-500">{(data.costs || []).length} item{(data.costs || []).length === 1 ? "" : "s"}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setCostOpen(true)} data-testid="add-cost-btn"><Plus size={14} /> Add Cost</Button>
          </div>
          <div className="divide-y divide-slate-100">
            {(data.costs || []).length === 0 && <div className="px-5 py-6 text-sm text-slate-400 italic">No costs yet</div>}
            {(data.costs || []).map(c => (
              <div key={c.id} className="px-5 py-3 flex items-center justify-between" data-testid={`cost-row-${c.id}`}>
                <div className="text-sm text-slate-800">{c.name}</div>
                <div className="flex items-center gap-3">
                  <div className="font-medium text-slate-900 tabular-nums">{fmt(c.amount)}</div>
                  <button onClick={() => handleDeleteCost(c.id)} className="text-slate-300 hover:text-red-500 p-1" data-testid={`cost-delete-${c.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
            <div className="px-5 py-3 flex items-center justify-between bg-slate-50/50 border-t border-slate-200">
              <div className="text-xs uppercase tracking-wider text-slate-500">Total</div>
              <div className="font-heading font-bold text-slate-900 tabular-nums">{fmt(s.total || 0)}</div>
            </div>
          </div>
        </Card>

        {/* Payments */}
        <Card className="border-slate-200 shadow-none overflow-hidden" data-testid="payments-card">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="font-heading font-semibold text-slate-900">Payments</div>
              <div className="text-xs text-slate-500">{(data.payments || []).length} entr{(data.payments || []).length === 1 ? "y" : "ies"}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setPayOpen(true)} data-testid="add-payment-btn"><Plus size={14} /> Add Payment</Button>
          </div>
          <div className="divide-y divide-slate-100">
            {(data.payments || []).length === 0 && <div className="px-5 py-6 text-sm text-slate-400 italic">No payments yet</div>}
            {(data.payments || []).map(p => (
              <div key={p.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`payment-row-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-slate-200 bg-slate-50">{TYPE_LABEL[p.type] || p.type}</Badge>
                    <span className="font-medium text-slate-900 tabular-nums">{fmt(p.amount)}</span>
                    {p.paid ? (
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border-emerald-200">Paid</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-700 border-amber-200">Due</Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {p.due_date && <>Due {fmtDate(p.due_date)}</>}
                    {p.paid && p.payment_date && <> · paid {fmtDate(p.payment_date)}</>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => togglePaid(p)}
                    className={`px-2 py-1 rounded text-[11px] border transition-colors ${p.paid ? "border-slate-200 text-slate-500 hover:bg-slate-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}
                    data-testid={`payment-toggle-${p.id}`}
                    title={p.paid ? "Mark as unpaid" : "Mark as paid"}
                  >
                    <Check size={12} className="inline" /> {p.paid ? "Undo" : "Paid"}
                  </button>
                  <button onClick={() => handleDeletePayment(p.id)} className="text-slate-300 hover:text-red-500 p-1" data-testid={`payment-delete-${p.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Notes */}
      <Card className="border-slate-200 shadow-none" data-testid="notes-card">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-heading font-semibold text-slate-900">Notes</div>
          {notesDirty && (
            <Button size="sm" onClick={saveNotes} className="bg-blue-600 hover:bg-blue-700 text-white h-9" data-testid="notes-save-btn">Save</Button>
          )}
        </div>
        <div className="p-5">
          <Textarea
            value={notesDraft}
            onChange={(e) => { setNotesDraft(e.target.value); setNotesDirty(e.target.value !== (data.notes || "")); }}
            placeholder="Anything to remember about this retreat — schedules, dietary requirements, special arrangements..."
            rows={5}
            data-testid="notes-textarea"
          />
        </div>
      </Card>

      {/* Add cost dialog */}
      <Dialog open={costOpen} onOpenChange={(o) => !o && setCostOpen(false)}>
        <DialogContent className="max-w-sm" data-testid="cost-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Cost</DialogTitle>
            <DialogDescription className="sr-only">Add a cost item.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Item</Label>
              <Input value={newCost.name} onChange={(e) => setNewCost(v => ({ ...v, name: e.target.value }))} className="mt-1 h-11" placeholder="Accommodation, catering, transfer..." autoFocus data-testid="cost-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Amount (€)</Label>
              <Input type="number" step="0.01" min="0" value={newCost.amount} onChange={(e) => setNewCost(v => ({ ...v, amount: e.target.value }))} className="mt-1 h-11" placeholder="0.00" data-testid="cost-amount-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCostOpen(false)}>Cancel</Button>
            <Button onClick={handleAddCost} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="cost-save-btn">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add payment dialog */}
      <Dialog open={payOpen} onOpenChange={(o) => !o && setPayOpen(false)}>
        <DialogContent className="max-w-sm" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Payment</DialogTitle>
            <DialogDescription className="sr-only">Add a payment record.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Type</Label>
              <Select value={newPay.type} onValueChange={(v) => setNewPay(p => ({ ...p, type: v }))}>
                <SelectTrigger className="mt-1 h-11" data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prepayment">Prepayment</SelectItem>
                  <SelectItem value="final">Final</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Amount (€)</Label>
                <Input type="number" step="0.01" min="0" value={newPay.amount} onChange={(e) => setNewPay(p => ({ ...p, amount: e.target.value }))} className="mt-1 h-11" placeholder="0.00" data-testid="payment-amount-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Due date</Label>
                <Input type="date" value={newPay.due_date} onChange={(e) => setNewPay(p => ({ ...p, due_date: e.target.value }))} className="mt-1 h-11" data-testid="payment-due-input" />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={newPay.paid} onChange={(e) => setNewPay(p => ({ ...p, paid: e.target.checked }))} className="h-4 w-4" data-testid="payment-paid-check" />
              <span className="text-sm text-slate-700">Mark as paid now</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={handleAddPayment} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="payment-save-btn">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit retreat */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md" data-testid="retreat-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Retreat</DialogTitle>
            <DialogDescription className="sr-only">Update retreat name, dates and participants.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
              <Input
                value={editDraft.name}
                onChange={(e) => setEditDraft(v => ({ ...v, name: e.target.value }))}
                className="mt-1 h-11"
                autoFocus
                data-testid="retreat-edit-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Start date</Label>
                <Input
                  type="date"
                  value={editDraft.start_date}
                  onChange={(e) => setEditDraft(v => ({ ...v, start_date: e.target.value }))}
                  className="mt-1 h-11"
                  data-testid="retreat-edit-start"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">End date</Label>
                <Input
                  type="date"
                  value={editDraft.end_date}
                  onChange={(e) => setEditDraft(v => ({ ...v, end_date: e.target.value }))}
                  className="mt-1 h-11"
                  data-testid="retreat-edit-end"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Participants</Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={editDraft.participants}
                onChange={(e) => setEditDraft(v => ({ ...v, participants: e.target.value }))}
                className="mt-1 h-11"
                data-testid="retreat-edit-participants"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="retreat-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete retreat */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md" data-testid="retreat-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete retreat?</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              This will permanently remove <span className="font-semibold text-slate-900">{data.name}</span> with all its costs ({(data.costs || []).length}) and payments ({(data.payments || []).length}). This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} data-testid="retreat-delete-cancel">Cancel</Button>
            <Button onClick={handleDeleteRetreat} className="bg-red-600 hover:bg-red-700 text-white" data-testid="retreat-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
